import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Transparence() {
  const [heroRef, heroVis] = useReveal(0.1);
  const [introRef, introVis] = useReveal(0.15);
  const [cardRef, cardVis] = useReveal(0.1);
  const [engRef, engVis] = useReveal(0.15);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/Sylvie-SMP_13.jpg" alt="Pierres et certificats"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.3) contrast(1.1) saturate(0.8)' }} />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.4)' }} />

        <div ref={heroRef} className={`relative z-10 text-center px-6 transition-all duration-[1.5s] ${heroVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-10 h-px bg-white/20" />
            <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
            <div className="w-10 h-px bg-white/20" />
          </div>
          <p className="text-xs tracking-[0.5em] uppercase mb-6 font-light text-white/50">Notre Engagement</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light tracking-wider mb-6">
            La Transparence
          </h1>
          <p className="text-white/35 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            Savoir d'où viennent les pierres et l'or, c'est respecter ce qu'ils deviennent.
          </p>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="py-24 md:py-32 px-6 bg-white">
        <div ref={introRef} className={`max-w-3xl mx-auto text-center transition-all duration-[1.5s] ${introVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-10 leading-tight">
            La confiance est au cœur<br />de chaque création
          </h2>
          <div className="space-y-6 text-gray-500 text-base md:text-lg leading-[1.9] max-w-2xl mx-auto">
            <p>
              Chez Pernety, nous croyons qu'un bijou précieux mérite une histoire transparente.
              Chaque pierre est accompagnée de ses certificats d'authenticité. Chaque gramme d'or
              est traçable.
            </p>
            <p>
              Les certificats d'or montrent d'où viennent les pierres et d'où vient l'or.
              C'est notre promesse : une joaillerie responsable et authentique.
            </p>
          </div>
        </div>
      </section>

      {/* ── LES ENGAGEMENTS — 3 PILIERS ── */}
      <section ref={cardRef} className="py-20 md:py-28 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.5em] uppercase font-light text-gray-400 mb-4">Nos Engagements</p>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Trois piliers de confiance</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                ),
                title: 'Certificats d\'authenticité',
                desc: 'Chaque pierre précieuse est accompagnée d\'un certificat délivré par un laboratoire de gemmologie reconnu, garantissant son origine, sa nature et ses caractéristiques.',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                ),
                title: 'Traçabilité des pierres',
                desc: 'Nous connaissons le parcours de chaque pierre, depuis les mines et les marchés de Bangkok jusqu\'à l\'atelier. Une chaîne d\'approvisionnement courte et maîtrisée.',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                ),
                title: 'Or traçable',
                desc: 'L\'or utilisé dans nos créations est certifié et traçable. Les certificats attestent de sa provenance et garantissent une filière responsable.',
              },
            ].map((item, i) => (
              <div key={i}
                className={`bg-white p-10 text-center transition-all duration-1000 ${cardVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${i * 150}ms` }}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gray-200 text-gray-900 mb-6">
                  {item.icon}
                </div>
                <h3 className="font-serif text-xl text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT SECTION IMMERSIVE ── */}
      <section className="relative py-0 bg-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[70vh]">
          <div ref={engRef}
            className={`md:col-span-7 flex items-center px-8 md:px-16 lg:px-20 py-20 md:py-0 transition-all duration-[1.5s] ${engVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-white/30" />
                <p className="text-xs tracking-[0.4em] uppercase font-medium text-white/40">Notre Promesse</p>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-10 leading-tight">
                Un bijou précieux<br />mérite une histoire<br />transparente
              </h2>

              <p className="text-white/40 leading-[1.9] text-[15px] mb-8 max-w-lg">
                Nous nous engageons à fournir, pour chaque création, l'ensemble des informations
                relatives à la provenance des matériaux. Parce que la beauté d'un bijou
                réside aussi dans l'intégrité de son histoire.
              </p>

              <div className="flex items-start gap-8 pt-8 border-t border-white/10">
                <div>
                  <p className="font-serif text-3xl text-white mb-1">100%</p>
                  <p className="text-white/30 text-xs tracking-wider">Pierres certifiées</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-white mb-1">100%</p>
                  <p className="text-white/30 text-xs tracking-wider">Or traçable</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-white mb-1">100%</p>
                  <p className="text-white/30 text-xs tracking-wider">Artisanat</p>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 relative overflow-hidden min-h-[400px]">
            <img src="/photos/Sylvie-SMP_14.jpg" alt="Certificats et pierres"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.6) contrast(1.1)' }} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6">
            Une question sur nos engagements ?
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mb-10">
            N'hésitez pas à nous contacter pour en savoir plus sur la provenance
            de nos pierres et matériaux.
          </p>
          <Link to="/contact"
            className="inline-block px-12 py-5 bg-black text-white text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-gray-800">
            Nous contacter
          </Link>
        </div>
      </section>
    </div>
  );
}
